<template>
  <a
    class="map"
    :href="mapUrl"
    :target="linkTarget"
    data-test-id="map-link"
    @click="$emit('map-link-click')"
  >
    <picture class="map__picture">
      <source
        v-for="breakpoint in screenSizes"
        :key="breakpoint.minWidth"
        :srcset="getSources(breakpoint.imageSize, true)"
        :media="getMediaQuery(breakpoint.minWidth)"
        data-test-id="map-source"
      >
      <img :src="getDefaultSource()" :alt="altText" class="map__img" data-test-id="map-img">
      <EMapboxLogo
        class="mapbox-logo"
        :class="{'mapbox-logo--extra-margin': extraMargin}"
        data-test-id="mapbox-logo"
      />
    </picture>
    <slot />
  </a>
</template>
<script>
import { mapState, mapGetters } from 'vuex';

import { getUrlQuery } from '~~/common/js/universal/search';

// these markets have the new Maps vertical enabled
// and users should be routed to /maps rather than /map
const mapsMarkets = [];

const isValidCoordinate = (coordinate) => typeof coordinate.latitude === 'number' && typeof coordinate.longitude === 'number';

const defaultScreenSize = { minWidth: 400, imageSize: '660x150' };

const zoomSizes = {
  country: 3,
  region: 5,
  place: 8,
  postcode: 10,
  district: 10,
  locality: 11,
  neighborhood: 11,
  address: 11,
  poi: 12,
};

const getZoom = (placeTypes) => {
  if (placeTypes.length === 1) {
    return zoomSizes[placeTypes[0]];
  }
  // gets the bigger zoom
  const reducer = (key1, key2) => (zoomSizes[key1] >= zoomSizes[key2] ? key1 : key2);
  return zoomSizes[placeTypes.reduce(reducer, 'country')];
};

export default {
  components: {
    EMapboxLogo: async () => import(
      /* webpackPrefetch: true */
      /* webpackChunkName: "mapbox" */
      '~/assets/img/mapbox.svg'
    ),
  },
  props: {
    // Array of array. Each coordinate is an Array [longitude, latitude]
    markerCoordinates: {
      type: Array,
      required: true,
      validator: (value) => Array.isArray(value) &&
        value.every(isValidCoordinate),
    },
    screenSizes: {
      type: Array,
      default: () => [{ minWidth: 400, imageSize: '660x150' }],
    },
    placeTypes: {
      type: Array,
      required: true,
    },
    locationName: {
      type: String,
      default: '',
    },
    pinColor: {
      type: String,
      default: 'ff7950', // coral-30
    },
    numberedPins: {
      type: Boolean,
      default: false,
    },
    url: {
      type: String,
      default: '',
    },
    extraMargin: {
      type: Boolean,
      default: false,
    },
    alt: {
      type: String,
      default: '',
    },
  },
  computed: {
    ...mapState('user', ['bingMarketCode']),
    ...mapGetters('user', ['linkTarget']),
    altText() {
      if (this.alt !== '') {
        return this.alt;
      }
      return `${this.$t('web.snippet.maps.title')} ${this.locationName}`;
    },
    zoom() {
      if (this.markerCoordinates.length > 1) {
        return 'auto';
      }
      return `${this.centerLonLat},${getZoom(this.placeTypes)}`;
    },
    centerLonLat() {
      return `${this.markerCoordinates[0].longitude},${this.markerCoordinates[0].latitude}`;
    },
    centerLatLon() {
      return `${this.markerCoordinates[0].latitude},${this.markerCoordinates[0].longitude}`;
    },
    // the "Markers string" is the portion of the request which defines one or more markers
    markersString() {
      if (this.numberedPins) {
        return this.markerCoordinates
          .map((coordinate, index) => `pin-s-${index + 1}+${this.pinColor}(${coordinate.longitude},${coordinate.latitude})`)
          .join(',');
      }
      return this.markerCoordinates
        .map((coordinate) => `pin-s+${this.pinColor}(${coordinate.longitude},${coordinate.latitude})`)
        .join(',');
    },
    // used only in case the map points to a map vertical
    linkToMap() {
      // creating URL to map
      const query = {
        ...getUrlQuery(this.$route.query, this.$store.state.user),
        coordinates: this.centerLatLon,
        placeName: this.locationName,
      };

      const path = mapsMarkets.includes(this.bingMarketCode) ?
        'maps' :
        'map';
      return this.$router.resolve({ path, query }).route.fullPath;
    },
    mapUrl() {
      if (this.url !== '') {
        return this.url;
      }

      return this.linkToMap;
    },
  },
  methods: {
    getMediaQuery(breakpoint) {
      return `(min-width: ${breakpoint}px)`;
    },
    getSources(size, retina) {
      const url = new URL(this.$config.apiUrl);
      // Mapbox expects longitude first
      url.pathname = `/v1/proxy/location/map/${this.markersString}/${this.zoom}/${size}`;
      url.searchParams.set('logo', 'false');
      url.searchParams.set('attribution', 'false');

      if (!retina) {
        return url.toString();
      }

      const url2x = new URL(this.$config.apiUrl);
      url2x.pathname = `${url.pathname}@2x`;
      url2x.searchParams.set('logo', 'false');
      url2x.searchParams.set('attribution', 'false');
      return `${url2x.toString()} 2x, ${url.toString()} 1x`;
    },
    getDefaultSource() {
      return this.getSources(defaultScreenSize.imageSize);
    },
  },
};
</script>
<style lang="scss" scoped>
.map {
  overflow: hidden;
  width: 100%;
}

.map__picture {
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  width: 100%;
  height: 100%;
  overflow: hidden;
}

.map__img {
  object-fit: cover;
  width: 100%;
  height: 100%;
}

.mapbox-logo {
  position: relative;
  top: -$space-l;
  width: 65px;
}

.mapbox-logo--extra-margin {
  @include desktop {
    top: -$space-1l;
  }
}
</style>
